import { useLayoutEffect, useState } from 'react';

/** Workbench width (px) at which the picker splits into two panes. */
export const WORKBENCH_SPLIT_MIN_WIDTH = 640;

/**
 * Viewport offset for the builders' pinned side rails (outcome list, docked
 * items tray): fixed AppBar (64px) + the app shell's main top padding
 * (16-24px). Rails stick at this offset and cap at the viewport bottom, so
 * their scrollboxes are always fully on screen while the page scrolls.
 */
export const RAIL_TOP_PX = 88;

/**
 * Builder width (px) at which the picked-items tray docks as a persistent
 * right column instead of floating over the picker. 1024 = the picker's
 * split minimum (640) + the docked column (360) + the grid gap — so whenever
 * the tray is docked, the picker beside it still has room to split.
 */
export const ITEMS_DOCK_MIN_WIDTH = 1024;

/**
 * Fixed horizontal chrome between the viewport and the builder's measured box
 * on builder-canvas routes at sm+: app shell main padding (24) + shell
 * Container (0 — gutters zeroed on builder canvases) + page Container
 * gutters (32) + host Paper padding (32). Horizontal — unrelated to
 * RAIL_TOP_PX, which is a vertical offset that happens to also be 88.
 */
export const BUILDER_CANVAS_CHROME_PX = 88;

/**
 * Minimum viewport width at which the picked-items tray docks, for a given
 * nav width. Lets useNavRail decide whether collapsing to the icon rail is
 * what makes the tray dock: 1184px with the 72px rail (below the lg
 * breakpoint where the nav rails anyway, so a guard rather than a visible
 * boundary) vs 1352px with the 240px drawer. The shell's xl Container cap
 * (1536) still leaves the builder ≥1472px — it can never flip the dock
 * decision, so it is ignored here.
 */
export const builderCanvasDockMinViewport = (navWidthPx: number): number =>
  ITEMS_DOCK_MIN_WIDTH + navWidthPx + BUILDER_CANVAS_CHROME_PX;

/**
 * Measures an element and reports whether it is at least `minWidth` wide.
 * Keys on the element's own measured width, NOT viewport breakpoints — the
 * builder pane is squeezed by the surrounding shell (the nav width plus
 * BUILDER_CANVAS_CHROME_PX of padding), so viewport width is a poor proxy.
 *
 * `ref` is a callback ref so the observer re-attaches when the measured box
 * unmounts and remounts (e.g. toggling to "Reuse past work" and back). Without
 * ResizeObserver (jsdom) the initial `guess` stands and never updates →
 * tests deterministically exercise the narrow layout.
 */
function useMeasuredMinWidth(
  minWidth: number,
  guess: () => boolean
): { ref: (node: HTMLDivElement | null) => void; isWide: boolean } {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [isWide, setIsWide] = useState(guess);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !element) return;
    const measure = () => {
      setIsWide(element.offsetWidth >= minWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, minWidth]);

  return { ref: setElement, isWide };
}

/**
 * Whether the workbench wrapper is wide enough for the two-pane
 * (outcomes | templates) split. The pre-measure heuristic makes the first
 * paint usually right (a 1200px viewport yields a ≈660px workbench beside
 * the docked tray under the builder shell: the 72px rail +
 * BUILDER_CANVAS_CHROME_PX); the observer corrects it on mount.
 */
export function useWorkbenchSplit(): {
  ref: (node: HTMLDivElement | null) => void;
  isSplit: boolean;
} {
  const { ref, isWide } = useMeasuredMinWidth(
    WORKBENCH_SPLIT_MIN_WIDTH,
    () => typeof window !== 'undefined' && window.innerWidth >= 1200
  );
  return { ref, isSplit: isWide };
}

/**
 * Whether the Builder is wide enough to dock the picked-items tray as an
 * always-visible right column (playlist-builder shape) beside the picker.
 * Floating mode overlays the picker's preview actions on desktop, so wide
 * builders dock instead. The ≥1200px viewport heuristic corresponds to a
 * ≈1040px builder under the builder shell (the 72px rail +
 * BUILDER_CANVAS_CHROME_PX); the observer corrects it on mount.
 */
export function useItemsDock(): {
  ref: (node: HTMLDivElement | null) => void;
  isDocked: boolean;
} {
  const { ref, isWide } = useMeasuredMinWidth(
    ITEMS_DOCK_MIN_WIDTH,
    () => typeof window !== 'undefined' && window.innerWidth >= 1200
  );
  return { ref, isDocked: isWide };
}
